import { I18n } from "@assets/strings";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Group } from "@domain/group/group";
import { LinkIcon, LogoutIcon, RepostIcon } from "@ui/components/icons";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { MenuRow } from "@ui/components/menus/menuRow";
import { IconWrapper, SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { EditReferencePostModal } from "@ui/components/post/reference/editReferencePostModal";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { createToast } from "@ui/toasts/utils";

interface GroupMenuProps extends React.HTMLAttributes<HTMLDivElement> {
	group: Group;
}

export const GroupMenu: React.FC<GroupMenuProps> = ({ group, ...htmlProps }) => {
	const { trackingService } = useServices();
	const askDeleteConfirmation = useConfirmation(
		I18n.t(`group.${group.type}.delete.title`),
		I18n.t("delete"),
		group.type === "SHAREROOM" ? I18n.t("shareroom.delete.description") : undefined
	);
	const askLeaveConfirmation = useConfirmation(
		group.status === "PENDING_HOST_ACCEPTANCE"
			? I18n.t(`group.${group.type}.cancel_request.title`)
			: I18n.t(`group.${group.type}.leave.title`),
		group.status === "PENDING_HOST_ACCEPTANCE" ? I18n.t("confirm") : I18n.t("quit"),
		I18n.t(`group.${group.type}.leave.message`)
	);

	const { createModal } = useModalTools();

	const { groupService } = useServices();
	const currentProfile = useCurrentProfile();
	const { owner } = group;
	const isOwner = currentProfile?.id === owner.id;
	const isOpen = group.type === "DISCUSSION_GROUP" ? group.accessMode === "OPEN" : group.visibility === "PUBLIC";
	const isAdmin = group?.userRole === "ADMINISTRATOR";
	const canSendInvitations = group.type === "DISCUSSION_GROUP" && isOpen && !isAdmin;

	const history = useHistory();

	return !currentProfile ? null : (
		<KebabMenu {...htmlProps}>
			<SimpleMenuContent
				onEdit={isOwner ? () => history.push(Path.GroupEditor(group.id, group.type)) : undefined}
				onDelete={
					isOwner
						? () => {
								askDeleteConfirmation(async () => {
									try {
										await groupService.deleteGroup(group.id);
										history.push(Path.Root);
										createToast(I18n.t("success"), I18n.t(`group.${group.type}.toast.delete.success`), "success");
									} catch (e) {
										if (e instanceof Error && "code" in e && e.code == 401) {
											createToast(I18n.t("error"), I18n.t(`group.${group.type}.toast.delete.error`), "error");
											//createModal((close) => <UnauthorizeDeleteShareRoom close={close} />);
										} else {
											createToast(I18n.t("error"), I18n.t(`group.${group.type}.toast.delete.error`), "error");
										}
										//createModal((close) => <UnauthorizeDeleteShareRoom close={close} />)
										//createToast(I18n.t("error"), I18n.t(`group.${group.type}.toast.delete.error`), "error");
									}
								});
							}
						: undefined
				}
				reportInfos={
					isOwner
						? undefined
						: {
								contentId: group.id,
								contentType: "GROUP",
							}
				}
			>
				{isOwner
					? null
					: ((!!group.userRole && group.status === "VALIDATED") ||
							group.status === "PENDING_HOST_ACCEPTANCE" ||
							group.status === "PENDING_GUEST_ACCEPTANCE") && (
							<MenuRow
								onClick={() => {
									askLeaveConfirmation(async () => {
										await groupService.leaveGroup(group.id);
										history.push(Path.Root);
										createToast(I18n.t("success"), I18n.t(`group.${group.type}.toast.leave.success`), "success");
									});
								}}
							>
								<IconWrapper>
									<LogoutIcon />
								</IconWrapper>
								<span>
									{group.status === "PENDING_HOST_ACCEPTANCE"
										? I18n.t("group.menu.cancel_request")
										: I18n.t(`group.${group.type}.menu.leave`)}
								</span>
							</MenuRow>
						)}
				{group.visibility !== "PRIVATE" && (
					<MenuRow
						onClick={() => {
							if (group.type === "SHAREROOM") trackingService.track("Share classroom");
							createModal((close) => (
								<EditReferencePostModal close={close} shared={{ type: "SHARE_GROUP", reference: group }} />
							));
						}}
					>
						<IconWrapper>
							<RepostIcon />
						</IconWrapper>
						<span>{I18n.t("share")}</span>
					</MenuRow>
				)}
				{canSendInvitations && (
					<MenuRow
						onClick={() => {
							navigator.clipboard.writeText(window.location.href);
							createToast(I18n.t("success"), I18n.t(`group.${group.type}.toast.copied_link`), "success");
						}}
					>
						<IconWrapper>
							<LinkIcon />
						</IconWrapper>
						<span>{I18n.t(`group.${group.type}.public.copy_link`)}</span>
					</MenuRow>
				)}
			</SimpleMenuContent>
		</KebabMenu>
	);
};
